// src/lib/productCategories.js
// ───────────────────────────────────────────────────────────────────────────
// 🟦 Categorías de producto + estrategia de precio (PLAN-INTAKE-PRODUCTOS, Fase A)
//
// Catálogo en dos niveles: la CATEGORÍA define la estrategia de precio/producción
// y qué atributos pide; el SKU (colección `products`) es la instancia reutilizable.
// "Config en datos": estas son las semillas por defecto; se pueden sobreescribir
// desde Firestore (`project_settings/productCategories`) sin tocar código.
//
// pricingStrategy (alimenta a futuro functions/lib/pricing.js — el cotizador):
//   - bom_geometry : caja/display → costo por geometría + materiales (BOM)
//   - area_ft2     : banner/vinilo → precio por pie² (ancho×alto)
//   - per_unit     : sticker → precio por unidad / pliego
//   - manual       : fallback → precio capturado a mano
// ───────────────────────────────────────────────────────────────────────────

/** Tipos de atributo que una categoría puede pedir.
 *  MATERIAL = selector conectado a la colección `materials` (no texto libre). */
export const ATTR = { TEXT: "text", NUMBER: "number", SELECT: "select", MATERIAL: "material", DIMENSIONS: "dimensions" };

/** Semillas por defecto (editables luego desde project_settings/productCategories). */
export const DEFAULT_PRODUCT_CATEGORIES = [
  {
    id: "caja",
    name: "Caja",
    icon: "Package",
    pricingStrategy: "bom_geometry",
    attributes: [
      { key: "dims", label: "Dimensiones", type: ATTR.DIMENSIONS, dims: ["length", "width", "height"] },
      { key: "material", label: "Material", type: ATTR.MATERIAL },
      { key: "boxStyle", label: "Estilo (RSC, mailer…)", type: ATTR.TEXT },
    ],
  },
  {
    id: "display",
    name: "Display / POP",
    icon: "LayoutPanelTop",
    pricingStrategy: "bom_geometry",
    attributes: [
      { key: "dims", label: "Dimensiones", type: ATTR.DIMENSIONS, dims: ["length", "width", "height"] },
      { key: "material", label: "Material", type: ATTR.MATERIAL },
    ],
  },
  {
    id: "banner",
    name: "Banner",
    icon: "Flag",
    pricingStrategy: "area_ft2",
    attributes: [
      { key: "dims", label: "Dimensiones", type: ATTR.DIMENSIONS, dims: ["length", "width"] },
      { key: "material", label: "Material (substrato)", type: ATTR.MATERIAL },
      { key: "finish", label: "Acabado", type: ATTR.SELECT, options: ["Ojetes", "Dobladillo", "Bolsillo", "Ninguno"] },
    ],
  },
  {
    id: "vinilo",
    name: "Vinilo",
    icon: "StickyNote",
    pricingStrategy: "area_ft2",
    attributes: [
      { key: "dims", label: "Dimensiones", type: ATTR.DIMENSIONS, dims: ["length", "width"] },
      { key: "material", label: "Material", type: ATTR.MATERIAL },
      { key: "lamination", label: "Laminado", type: ATTR.SELECT, options: ["Mate", "Brillante", "Ninguno"] },
    ],
  },
  {
    id: "sticker",
    name: "Sticker",
    icon: "Sticker",
    pricingStrategy: "per_unit",
    attributes: [
      { key: "dims", label: "Dimensiones", type: ATTR.DIMENSIONS, dims: ["length", "width"] },
      { key: "shape", label: "Forma", type: ATTR.SELECT, options: ["Cuadrado", "Redondo", "Troquelado"] },
    ],
  },
  {
    id: "servicio",
    name: "Servicio",
    icon: "Wrench",
    pricingStrategy: "per_unit",
    attributes: [
      { key: "detail", label: "Detalle del servicio", type: ATTR.TEXT },
    ],
  },
  {
    id: "accesorio",
    name: "Accesorio",
    icon: "Package",
    pricingStrategy: "per_unit",
    attributes: [
      { key: "material", label: "Material", type: ATTR.MATERIAL },
      { key: "detail", label: "Detalle", type: ATTR.TEXT },
    ],
  },
  {
    id: "otro",
    name: "Otro",
    icon: "Package",
    pricingStrategy: "per_unit",
    attributes: [
      { key: "detail", label: "Detalle", type: ATTR.TEXT },
    ],
  },
];

/**
 * Mezcla las categorías por defecto con un override de Firestore (si existe).
 * El override es un array con la misma forma; se reemplaza por id. PURO (testeable).
 * @param {Array} [override]  contenido de project_settings/productCategories.categories
 * @return {Array}
 */
export function resolveCategories(override) {
  if (!Array.isArray(override) || override.length === 0) return DEFAULT_PRODUCT_CATEGORIES;
  const byId = new Map(DEFAULT_PRODUCT_CATEGORIES.map((c) => [c.id, c]));
  for (const c of override) if (c && c.id) byId.set(c.id, { ...byId.get(c.id), ...c });
  return [...byId.values()];
}

/** Busca una categoría por id. PURO. */
export function getCategory(categories, id) {
  return (categories || []).find((c) => c.id === id) || null;
}
